import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertTriangle, Shield, Info } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SecurityTab = () => {
  const { t } = useLanguage();
  const [ipFlags, setIpFlags] = useState([]);
  const [showClearDialog, setShowClearDialog] = useState(false);

  const fetchIpFlags = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ip-flags`, { credentials: 'include' });
      if (res.ok) setIpFlags(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchIpFlags();
  }, []);

  const handleClearIpFlags = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employer/ip-flags/clear`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { 
        toast.success("IP flags cleared and archived"); 
        fetchIpFlags(); 
        setShowClearDialog(false);
      }
    } catch (e) { toast.error("Failed to clear"); }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              {t("ipFlags") || "IP Flags"} {ipFlags.length > 0 && <span className="text-muted-foreground font-normal text-base">({ipFlags.length})</span>}
            </CardTitle>
            <CardDescription>{t("ipFlagExplanation") || "IP flags detect when multiple employees check in from the same device/network. This helps identify potential buddy punching or time fraud."}</CardDescription>
          </div>
          {ipFlags.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowClearDialog(true)} className="text-destructive">
              {t("clearAll") || "Clear All"}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {ipFlags.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">{t("noSecurityAlerts") || "No security alerts"}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("noSecurityAlertsHelper") || "No suspicious check-in activity detected"}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ipFlags.map((flag) => (
                <IpFlagItem key={flag.flag_id} flag={flag} t={t} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clearIpFlags") || "Clear All IP Flags?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("clearIpFlagsDesc") || "This will archive all IP flags. They will still be available in the Audit History tab for compliance and review purposes."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearIpFlags} className="bg-destructive text-destructive-foreground">
              {t("clearAll") || "Clear All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const IpFlagItem = ({ flag, t }) => {
  return (
    <div className="p-4 rounded-xl bg-destructive/5 border border-destructive/20">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium mb-2">{t("multipleLoginsFromDevice") || "Multiple logins from same device"}</p>
          <p className="text-sm text-muted-foreground mb-2">{t("ipAddress") || "IP"}: <span className="font-mono">{flag.device_ip}</span></p>
          <div className="flex flex-wrap gap-2">
            {flag.users?.map((u) => (
              <Badge key={u.user_id} variant="secondary">{u.name}</Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;
